import { Injectable } from '@angular/core';

export interface FormMetadata {
  signees?: any[];
  medical_notes?: Array<{
    date?: string;
    employee_id?: string;
    medical_notes?: string;
  }>;
  medical_history?: Record<string, any>;
  laser?: any[];
  treatment_id?: string;
  demographics?: Record<string, any>;
  primary_insurance?: any[];
  secondary_insurance?: any[];
  tertiary_insurance?: any[];
  appointmentId?: string | number;
  location_remote_id?: string | number;
  shots?: any[];
  soap_notes?: {
    subjective_notes?: any[];
    objective_notes?: any[];
    assessment_notes?: any[];
    plan_notes?: any[];
  };
  equipments?: Array<{
    id?: string | number | null;
    equipment_type?: string;
    time?: number | null;
    quantity?: number | null;
    unit?: number | null;
    notes?: string;
    attributes?: string[];
  }>;
  treatment_notes?: Array<{ amount?: string | number; service_id?: string | number }>;
  [key: string]: any;
}

export interface SigneeMetadata {
  widget_id?: string;
  signee_type?: string;
  signee_id?: any;
  sign_image?: string;
  signed_at?: string;
  full_name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FormMetadataExtractorService {
  constructor() {}

  extractMetadata(presenterComponent: any): FormMetadata | null {
    if (!presenterComponent) {
      return null;
    }

    const metadata = presenterComponent.getMetadata();

    if (!metadata || typeof metadata !== 'object') {
      return null;
    }

    return this.processMetadata(metadata);
  }

  processMetadata(metadata: any): FormMetadata | null {
    if (!metadata || typeof metadata !== 'object') {
      return null;
    }

    const hasSignees =
      'signees' in metadata && Array.isArray(metadata.signees) && metadata.signees?.length > 0;

    const hasMedicalHistory =
      'medical_history' in metadata &&
      metadata.medical_history &&
      Object.keys(metadata.medical_history).length > 0;

    const hasMedicalNotes =
      'medical_notes' in metadata &&
      Array.isArray(metadata.medical_notes) &&
      metadata.medical_notes?.length > 0;

    const hasLaser =
      'laser' in metadata && Array.isArray(metadata.laser) && metadata.laser?.length > 0;

    const hasDemographics =
      'demographics' in metadata && Object.keys(metadata.demographics || {}).length > 0;

    const hasPrimaryInsurance =
      'primary_insurance' in metadata &&
      metadata.primary_insurance &&
      Object.keys(metadata.primary_insurance).length > 0;

    const hasSecondaryInsurance =
      'secondary_insurance' in metadata &&
      Array.isArray(metadata.secondary_insurance) &&
      metadata.secondary_insurance.length > 0;

    const hasTertiaryInsurance =
      'tertiary_insurance' in metadata &&
      Array.isArray(metadata.tertiary_insurance) &&
      metadata.tertiary_insurance.length > 0;

    const hasAppointmentId =
      ('appointmentId' in metadata || 'appointment_id' in metadata) &&
      metadata['appointment_id'] !== null &&
      metadata['appointment_id'] !== undefined &&
      metadata['appointment_id'] !== '';

    const hasLocationRemoteId =
      metadata.location_remote_id !== null &&
      metadata.location_remote_id !== undefined &&
      metadata.location_remote_id !== '';

    const hasShots =
      'shots' in metadata && Array.isArray(metadata.shots) && metadata.shots.length > 0;

    const hasSoapNotes = this.hasSoapNotesData(metadata.soap_notes);

    const hasEquipment = Array.isArray(metadata.equipments) && metadata.equipments.length > 0;

    const hasTreatmentNotes =
      'treatment_notes' in metadata &&
      Array.isArray(metadata.treatment_notes) &&
      metadata.treatment_notes.length > 0;

    const hasAmendment = 'amendment' in metadata && !!metadata.amendment;

    if (
      hasSignees ||
      hasMedicalHistory ||
      hasMedicalNotes ||
      hasLaser ||
      hasDemographics ||
      hasPrimaryInsurance ||
      hasSecondaryInsurance ||
      hasTertiaryInsurance ||
      hasAppointmentId ||
      hasLocationRemoteId ||
      hasShots ||
      hasSoapNotes ||
      hasEquipment ||
      hasTreatmentNotes ||
      hasAmendment
    ) {
      return metadata;
    }

    return null;
  }

  hasMetadata(metadata: FormMetadata | null): boolean {
    if (!metadata) {
      return false;
    }

    const hasSignees = Array.isArray(metadata.signees) && metadata.signees.length > 0;
    const hasMedicalHistory =
      metadata.medical_history && Object.keys(metadata.medical_history).length > 0;
    const hasMedicalNotes = metadata.medical_notes && metadata.medical_notes.length > 0;
    const hasLaser = Array.isArray(metadata.laser) && metadata.laser.length > 0;
    const hasDemographics = metadata.demographics && Object.keys(metadata.demographics).length > 0;
    const hasPrimaryInsurance =
      metadata.primary_insurance && Object.keys(metadata.primary_insurance).length > 0;
    const hasSecondaryInsurance =
      Array.isArray(metadata.secondary_insurance) && metadata.secondary_insurance.length > 0;
    const hasTertiaryInsurance =
      Array.isArray(metadata.tertiary_insurance) && metadata.tertiary_insurance.length > 0;

    const hasAppointmentId =
      metadata['appointment_id'] !== null &&
      metadata['appointment_id'] !== undefined &&
      metadata['appointment_id'] !== '';
    const hasLocationRemoteId =
      metadata.location_remote_id !== null &&
      metadata.location_remote_id !== undefined &&
      metadata.location_remote_id !== '';
    const hasShots = metadata.shots && metadata.shots.length > 0;
    const hasSoapNotes = this.hasSoapNotesData(metadata.soap_notes);
    const hasEquipment = Array.isArray(metadata.equipments) && metadata.equipments.length > 0;
    const hasTreatmentNotes =
      Array.isArray(metadata.treatment_notes) && metadata.treatment_notes.length > 0;

    return !!(
      hasSignees ||
      hasMedicalHistory ||
      hasMedicalNotes ||
      hasLaser ||
      hasDemographics ||
      hasPrimaryInsurance ||
      hasSecondaryInsurance ||
      hasTertiaryInsurance ||
      hasAppointmentId ||
      hasLocationRemoteId ||
      hasShots ||
      hasSoapNotes ||
      hasEquipment ||
      hasTreatmentNotes ||
      Object.keys(metadata).length > 0
    );
  }

  addMetadataToPayload(basePayload: any, presenterComponent: any): any {
    const metadata = this.extractMetadata(presenterComponent);

    if (this.hasMetadata(metadata)) {
      return {
        ...basePayload,
        data: {
          ...basePayload.data,
          metadata: metadata,
        },
      };
    }

    return basePayload;
  }

  private hasSoapNotesData(soapNotes: any): boolean {
    if (!soapNotes || typeof soapNotes !== 'object') {
      return false;
    }

    const listKeys = [
      'subjective_notes',
      'objective_notes',
      'assessment_notes',
      'plan_notes',
      'subjective_notes_attributes',
      'objective_notes_attributes',
      'assessment_notes_attributes',
      'plan_notes_attributes',
      'plan_items',
    ];

    return listKeys.some((key) => Array.isArray(soapNotes[key]) && soapNotes[key].length > 0);
  }

  extractSignatures(formData: any, form?: any): SigneeMetadata[] {
    if (!formData || !formData.sections) {
      return [];
    }

    const DEFAULT_EMPTY_IMAGE =
      'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

    try {
      return formData.sections.flatMap((section) =>
        section.widgets
          .filter((widget) => widget.name === 'fb-signature' && widget.properties.id?.value)
          .map((widget) => {
            const signImage = widget.properties.value?.value;
            const signee_id = this.getSignatureIdByWidgetId(widget.properties.id?.value, form);

            if (
              !signImage ||
              signImage === DEFAULT_EMPTY_IMAGE ||
              signImage === widget.properties.value?.original
            ) {
              return {
                widget_id: widget.properties.id?.value,
                signee_type: widget.properties.signType.value,
                signee_id: signee_id,
              };
            }

            const encodedSignImage = encodeURIComponent(signImage);
            return {
              widget_id: widget.properties.id?.value,
              signee_type: widget.properties.signType.value,
              sign_image: encodedSignImage,
              signed_at: widget.properties.signType?.signeeDate ?? '',
              full_name: widget.properties.signType?.signeeName ?? '',
              signee_id: signee_id,
            };
          })
          .filter(Boolean),
      );
    } catch (e) {
      console.error('ERROR: Signees extraction failed', e);
      return [];
    }
  }

  getSignatureIdByWidgetId(widgetId: string, form?: any): any {
    if (form && form.metadata && form.metadata.signees) {
      const signeeMeta = form.metadata.signees.find((signee) => {
        return signee.widget_id === widgetId;
      });
      return signeeMeta?.signee_id;
    }
    return undefined;
  }

  resetOriginalSignatures(formData: any): void {
    if (!formData || !formData.sections) {
      return;
    }

    formData.sections.forEach((section) => {
      section.widgets.forEach((widget) => {
        if (widget.name === 'fb-signature') {
          widget.properties.value.original = '';
        }
      });
    });
  }

  createFormDataWithSignatures(
    formData: any,
    personId?: number,
    templateId?: number,
    status: number = 2,
    sharedWithPatient: boolean = true,
    form?: any,
    presenterComponent?: any,
  ): any {
    const signees = this.extractSignatures(formData, form);

    this.resetOriginalSignatures(formData);

    const existingMetadata = presenterComponent ? this.extractMetadata(presenterComponent) : null;

    const formExistingMetadata = form?.metadata || {};

    const combinedMetadata = {
      ...formExistingMetadata,
      ...(existingMetadata || {}),
      signees: signees,
    };

    const payload = {
      patient: personId || undefined,
      template: templateId || undefined,
      form_data: JSON.stringify(formData),
      status: status,
      shared_with_patient: sharedWithPatient,
      metadata: combinedMetadata,
    };

    return payload;
  }

  createFormDataWithMetadata(
    presenterComponent: any,
    personId?: number,
    templateId?: number,
    status: number = 2,
    sharedWithPatient: boolean = true,
    form?: any,
  ): any {
    if (!presenterComponent || !presenterComponent.getFormData) {
      throw new Error('Invalid presenter component - must have getFormData method');
    }

    const formData = presenterComponent.getFormData();
    const signees = this.extractSignatures(formData, form);

    this.resetOriginalSignatures(formData);

    const existingMetadata = this.extractMetadata(presenterComponent);

    const combinedMetadata = {
      ...(existingMetadata || {}),
      signees: signees,
    };

    const payload = {
      patient: personId || undefined,
      template: templateId || undefined,
      form_data: JSON.stringify(formData),
      status: status,
      shared_with_patient: sharedWithPatient,
      metadata: combinedMetadata,
    };

    return payload;
  }
}
